import React, { FC, useId } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import { FieldPath } from 'react-hook-form/dist/types/path';
import { Col, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';

import { noop } from 'lodash-es';
import ErrorOutlineIcon from 'material-react-icons/ErrorOutline';

import useTranslate from '../../hooks/useTranslate';
import Button from '../Button/Button';
import { GolfIdVerificationFormFieldValues } from './types';

const GolfIdVerificationInlineForm: FC = () => {
  const { translate } = useTranslate();
  const methods = useFormContext<GolfIdVerificationFormFieldValues>();
  const {
    register: baseRegister,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;
  const id = useId();

  // react-hook-form expect innerRef prop, but reactstrap provides only ref prop, so let's re-define register callback in our way
  const register = <TFieldName extends FieldPath<GolfIdVerificationFormFieldValues>>(
    name: TFieldName,
    options?: RegisterOptions<GolfIdVerificationFormFieldValues, TFieldName>
  ) => {
    const { ref: innerRef, ...rest } = baseRegister(name, options);

    return {
      innerRef,
      ...rest,
    };
  };

  return (
    <form onSubmit={handleSubmit(noop)}>
      <p>{translate('GolfIdVerification.prompt')}</p>
      <Row className={'g-3'}>
        {(['golfId', 'password'] as Array<keyof GolfIdVerificationFormFieldValues>).map(key => (
          <Col>
            <FormGroup>
              <Label for={`${id}_${key}`} className={'visually-hidden'}>
                {translate(`GolfIdVerification.labels.${key}`)}
              </Label>
              <Input
                {...register(key)}
                id={`${id}_${key}`}
                placeholder={String(translate(`GolfIdVerification.labels.${key}`))}
                invalid={!!errors[key]}
                disabled={isSubmitting}
              />
              <FormFeedback>{errors[key]?.message}</FormFeedback>
            </FormGroup>
          </Col>
        ))}
        <Col>
          <Button tertiary type={'submit'} disabled={isSubmitting} loading={isSubmitting}>
            {translate('GolfIdVerification.verify')}
          </Button>
        </Col>
      </Row>
      {errors.root && (
        <p className={'d-flex gap-2'}>
          <ErrorOutlineIcon />
          {errors.root.message}
        </p>
      )}
    </form>
  );
};

export default GolfIdVerificationInlineForm;
